'use client';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import { RedemptionButton } from '../RedemptionButton';

import styles from './styles.module.css';

import { type PartnerDisplay } from '@/modules/partners/types';

type Props = {
  hero: PartnerDisplay['hero'];
  cloudfrontHost: string;
  name: string;
  redemptionLink: string;
};

export function Hero({
  hero: { isSimple, images, transparentImage },
  cloudfrontHost,
  name,
  redemptionLink,
}: Props) {
  const t = useTranslations('partners');
  if (isSimple) {
    return (
      <section className={styles.heroSimple}>
        <img
          className={styles.heroSimpleImage}
          src={`${cloudfrontHost}/web/assets/partners/${images.desktop}`}
          srcSet={`${cloudfrontHost}/web/assets/partners/${images.desktop}`}
          // eslint-disable-next-line no-restricted-syntax
          data-testid="hero-image-desktop"
          alt=""
        />
        <Heading variant="lg">{t(`Partners.${name}.BlockTitle2`)}</Heading>
        <Body variant="sm-regular" className={styles.heroSimpleText}>
          {t(`Partners.${name}.SubTitle`)}
        </Body>
        <RedemptionButton
          ctaText={t(`Partners.${name}.ButtonGetTrial`)}
          redemptionLink={redemptionLink}
          // eslint-disable-next-line no-restricted-syntax
          data-testid="hero-button"
        />
      </section>
    );
  }

  return (
    <div className={styles.hero}>
      <picture className={styles.heroPicture}>
        <img
          className={styles.heroImage}
          src={`${cloudfrontHost}/web/assets/partners/red_gradient_bg.png`}
          alt=""
        />
        <img
          className={clsx(styles.heroImageMobile, {
            [styles.heroImageMobileFullHeight]:
              transparentImage || !images.mobile,
          })}
          src={`${cloudfrontHost}/web/assets/partners/gradient_bg_red_mweb.png`}
          alt=""
        />
        <img
          className={clsx(styles.heroImageSecondary, {
            [styles.heroImageSecondaryTransparent]: transparentImage,
          })}
          src={`${cloudfrontHost}/web/assets/partners/${images.desktop}`}
          // eslint-disable-next-line no-restricted-syntax
          data-testid="hero-image-desktop"
          alt=""
        />
        {images.mobile && (
          <img
            className={styles.heroImageSecondaryMobile}
            src={`${cloudfrontHost}/web/assets/partners/${images.mobile}`}
            // eslint-disable-next-line no-restricted-syntax
            data-testid="hero-image-mobile"
            alt=""
          />
        )}
      </picture>
      <div className={clsx(styles.section, styles.heroSection)}>
        <div
          className={clsx(styles.heroContent, {
            [styles.heroContentMobileNoMargin]: !images.mobile,
          })}
        >
          <Heading
            variant="lg"
            className={styles.heroContentTitle}
            // eslint-disable-next-line no-restricted-syntax
            data-testid="hero-title"
          >
            {t(`Partners.${name}.BlockTitle2`)}
          </Heading>
          <Body
            variant="sm-regular"
            className={styles.heroContentSubtitle}
            // eslint-disable-next-line no-restricted-syntax
            data-testid="hero-subtitle"
          >
            {t(`Partners.${name}.SubTitle`)}
          </Body>
          <RedemptionButton
            ctaText={t(`Partners.${name}.ButtonGetTrial`)}
            redemptionLink={redemptionLink}
            // eslint-disable-next-line no-restricted-syntax
            data-testid="hero-button"
          />
        </div>
      </div>
    </div>
  );
}
